import React, { useEffect, useLayoutEffect, useRef } from "react";

/* Steffi's face, driven live on top of the real product photograph.
 *
 * The geometry below is measured from the current product photo
 * (bot-front.webp): with the display radius normalised to 100, the eyes are
 * upright pills at x = ±32.6, y = +4.7, 19 wide and 38 tall; the brows are
 * thick soft arcs above them; the mouth is a smile 35 units wide at
 * y = +40.4.
 * Keeping those proportions is what makes the animated face read as the same
 * face that is printed in the photograph.
 */
const EYE_X = 32.6;
const EYE_Y = 4.7;
const EYE_W = 19.2;
const EYE_H = 37.8;
const MOUTH_Y = 40.4;
const MOUTH_HALF = 17.6;
const MOUTH_SAG = 12.5; // control-point depth: the curve itself dips half of it
/* left brow, mirrored for the right: outer end, curve, inner end */
const BROW = "M -50.2 -26.4 Q -38 -41.8 -22.4 -35.6";
const BROW_W = 6.6;
const MOUTH_W = 6.6;

/* warm cream strokes with an amber halo, like the display in the photo */
const GLOW_CORE = "#fff0d9";

const MAX_GAZE = 9;        // display units the face may travel
const EYE_PARALLAX = 1.12; // eyes lead the mouth a little, which reads as "looking"

/* A heart drawn around its own centre (about 18 × 17 units). It is scaled up
   to roughly the height of an eye, so the pills squash away and a heart of
   the same size pops in their place. */
const HEART =
  "M 0 6.4 C -1.8 3.6 -7.2 1.2 -8.8 -2.6 C -10.4 -6.6 -6.6 -10.2 -3.2 -8.2 " +
  "C -1.6 -7.2 -0.6 -5.6 0 -4.2 C 0.6 -5.6 1.6 -7.2 3.2 -8.2 " +
  "C 6.6 -10.2 10.4 -6.6 8.8 -2.6 C 7.2 1.2 1.8 3.6 0 6.4 Z";

/* ------------------------------------------------------------------ driver */
/* One rAF loop and one pointer listener for every face on the page. */

const faces = new Set();
const pointer = { x: 0.5, y: 0.4, active: false };
let running = false;

function tick(now) {
  faces.forEach((face) => face.frame(now));
  if (faces.size) {
    requestAnimationFrame(tick);
  } else {
    running = false;
  }
}

function start() {
  if (running) return;
  running = true;
  requestAnimationFrame(tick);
}

if (typeof window !== "undefined") {
  window.addEventListener(
    "pointermove",
    (e) => {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      pointer.active = true;
    },
    { passive: true }
  );
}

const clamp = (v, lo = -1, hi = 1) => Math.min(hi, Math.max(lo, v));
const lerp = (a, b, t) => a + (b - a) * t;

export default function SteffiFace({
  /** Amber, emissive display. When false the screen stays dark and asleep. */
  awake = true,
  /** Mouth works a little harder — used while Steffi is talking. */
  speaking = false,
  /** Her eyes turn to hearts. */
  hearts = false,
  /** Bumping this token makes Steffi glance towards `glanceDir` once. */
  glanceToken = 0,
  /** -1 = left, 1 = right, or a {x, y} pair. */
  glanceDir = 1,
  /** Follow the visitor's cursor. */
  followPointer = true,
  className = "",
  style,
}) {
  const rootRef = useRef(null);
  const groupRef = useRef(null);
  const eyesRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const mouthRef = useRef(null);

  const state = useRef({
    gx: 0, gy: 0,          // current gaze
    tx: 0, ty: 0,          // target gaze
    lid: 1,                // 1 = open, 0 = closed
    nextBlink: 0,
    blinkStart: -1,
    blinkQueue: 0,
    nextIdle: 0,
    idleUntil: 0,
    idle: { x: 0, y: 0 },
    rect: null,
    visible: true,
    speaking: false,
    hearts: false,
  });

  const glance = useRef({ token: glanceToken, until: 0, dir: { x: 0, y: 0 } });

  useLayoutEffect(() => {
    state.current.speaking = speaking;
  }, [speaking]);

  useLayoutEffect(() => {
    state.current.hearts = hearts;
  }, [hearts]);

  /* a fresh glance whenever the token changes */
  useEffect(() => {
    if (glance.current.token === glanceToken) return;
    glance.current.token = glanceToken;
    const d = typeof glanceDir === "number" ? { x: glanceDir, y: 0.18 } : glanceDir;
    glance.current.dir = { x: clamp(d.x), y: clamp(d.y ?? 0) };
    glance.current.until = performance.now() + 1500;
  }, [glanceToken, glanceDir]);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;

    const s = state.current;
    const measure = () => {
      s.rect = node.getBoundingClientRect();
    };
    measure();

    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const io = new IntersectionObserver(
      ([entry]) => {
        s.visible = entry.isIntersecting;
        if (entry.isIntersecting) measure();
      },
      { rootMargin: "120px" }
    );
    io.observe(node);

    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);

    /* Frames stop while the tab is hidden. Reset the lids so Steffi can never
       be found frozen mid-blink when the visitor comes back. */
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      s.blinkStart = -1;
      s.lid = 1;
      s.nextBlink = 0;
      [leftRef, rightRef].forEach((ref) => {
        ref.current?.setAttribute("height", String(EYE_H));
        ref.current?.setAttribute("y", String(100 + EYE_Y - EYE_H / 2));
      });
    };
    document.addEventListener("visibilitychange", onVisibility);

    const face = {
      frame(now) {
        if (!s.visible) return;
        if (!s.rect || s.rect.width === 0) measure();

        /* -------------------------------------------------- where to look */
        let tx = 0;
        let ty = 0;

        if (now < glance.current.until) {
          tx = glance.current.dir.x;
          ty = glance.current.dir.y;
        } else if (now < s.idleUntil) {
          tx = s.idle.x;
          ty = s.idle.y;
        } else if (followPointer && pointer.active && s.rect) {
          const cx = s.rect.left + s.rect.width / 2;
          const cy = s.rect.top + s.rect.height / 2;
          const reach = Math.max(window.innerWidth, window.innerHeight) * 0.42;
          tx = clamp((pointer.x - cx) / reach);
          ty = clamp((pointer.y - cy) / reach);
        }

        /* Steffi occasionally looks away of her own accord. */
        if (!s.nextIdle) s.nextIdle = now + 5200 + Math.random() * 5200;
        if (now > s.nextIdle && now > glance.current.until) {
          s.idle = { x: Math.random() < 0.5 ? -0.72 : 0.72, y: (Math.random() - 0.5) * 0.3 };
          s.idleUntil = now + 1100 + Math.random() * 700;
          s.nextIdle = s.idleUntil + 5200 + Math.random() * 6000;
        }

        s.tx = tx;
        s.ty = ty;
        const ease = reduced ? 1 : 0.045;
        s.gx = lerp(s.gx, s.tx, ease);
        s.gy = lerp(s.gy, s.ty, ease);

        /* ------------------------------------------------------- blinking */
        if (!s.nextBlink) s.nextBlink = now + 2200 + Math.random() * 3000;
        if (s.blinkStart < 0 && now > s.nextBlink && !reduced && !s.hearts) {
          s.blinkStart = now;
          s.blinkQueue = Math.random() < 0.22 ? 1 : 0; // the occasional double blink
        }
        if (s.blinkStart >= 0) {
          const t = now - s.blinkStart;
          const DOWN = 90;
          const UP = 150;
          if (t < DOWN) s.lid = 1 - t / DOWN;
          else if (t < DOWN + UP) s.lid = (t - DOWN) / UP;
          else {
            s.lid = 1;
            s.blinkStart = -1;
            if (s.blinkQueue > 0) {
              s.blinkQueue -= 1;
              s.nextBlink = now + 180;
            } else {
              s.nextBlink = now + 4800 + Math.random() * 3400;
            }
          }
        }

        /* ------------------------------------------------------- painting */
        const gx = s.gx * MAX_GAZE;
        /* little vertical travel, so eyes and mouth keep their spacing */
        const gy = s.gy * MAX_GAZE * 0.35;
        if (groupRef.current) {
          groupRef.current.setAttribute("transform", `translate(${gx.toFixed(2)} ${gy.toFixed(2)})`);
        }
        if (eyesRef.current) {
          const ex = gx * (EYE_PARALLAX - 1);
          const ey = gy * (EYE_PARALLAX - 1);
          eyesRef.current.setAttribute("transform", `translate(${ex.toFixed(2)} ${ey.toFixed(2)})`);
        }

        /* a blink squeezes the pills from both ends towards their middle */
        const lidH = Math.max(EYE_W * 0.32, EYE_H * s.lid);
        [leftRef, rightRef].forEach((ref) => {
          ref.current?.setAttribute("height", lidH.toFixed(2));
          ref.current?.setAttribute("y", (100 + EYE_Y - lidH / 2).toFixed(2));
          ref.current?.setAttribute("rx", Math.min(EYE_W / 2, lidH / 2).toFixed(2));
        });

        /* the mouth never sits perfectly still */
        const breathe = reduced ? 0 : Math.sin(now / 1750) * 0.9;
        const talk = s.speaking && !reduced ? Math.sin(now / 118) * 2.6 + 1.4 : 0;
        const sag = MOUTH_SAG + breathe + talk + (s.hearts ? 4.2 : 0);
        const width = MOUTH_HALF + (s.speaking ? -1.6 : 0) + (s.hearts ? 1.8 : 0);
        mouthRef.current?.setAttribute(
          "d",
          `M ${100 - width} ${100 + MOUTH_Y} Q 100 ${(100 + MOUTH_Y + sag).toFixed(2)} ${100 + width} ${100 + MOUTH_Y}`
        );
      },
    };

    faces.add(face);
    start();

    return () => {
      faces.delete(face);
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, [followPointer]);

  return (
    <svg
      ref={rootRef}
      viewBox="0 0 200 200"
      preserveAspectRatio="none"
      className={className}
      style={{
        display: "block",
        width: "100%",
        height: "100%",
        overflow: "visible",
        opacity: awake ? 1 : 0,
        transition: "opacity 900ms cubic-bezier(.2,.8,.2,1)",
        ...style,
      }}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <filter id="steffi-emissive" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="5" result="halo" />
          {/* the halo turns amber, the strokes stay warm cream */}
          <feColorMatrix
            in="halo"
            type="matrix"
            values="1 0 0 0 0  0 0.62 0 0 0  0 0 0.26 0 0  0 0 0 1.1 0"
            result="soft"
          />
          <feMerge>
            <feMergeNode in="soft" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <g ref={groupRef} filter="url(#steffi-emissive)">
        {/* Both sets of eyes share the parallax group, so the hearts drift
            with her gaze exactly as the eyes do. */}
        <g ref={eyesRef}>
          <g
            style={{
              opacity: hearts ? 0 : 1,
              transition: "opacity 260ms cubic-bezier(.2,.8,.2,1)",
            }}
          >
            {[
              [leftRef, -1],
              [rightRef, 1],
            ].map(([ref, side]) => (
              <rect
                key={side}
                ref={ref}
                x={100 + side * EYE_X - EYE_W / 2}
                y={100 + EYE_Y - EYE_H / 2}
                width={EYE_W}
                height={EYE_H}
                rx={EYE_W / 2}
                fill={GLOW_CORE}
                style={{
                  transformBox: "fill-box",
                  transformOrigin: "center",
                  transform: `scale(${hearts ? 1.15 : 1}, ${hearts ? 0.12 : 1})`,
                  transition: "transform 260ms cubic-bezier(.4,0,.2,1)",
                }}
              />
            ))}
          </g>

          {/* the brows stay, and lift a little while she is smitten */}
          <g
            style={{
              transform: `translateY(${hearts ? -4 : 0}px)`,
              transition: "transform 420ms cubic-bezier(.2,.8,.2,1)",
            }}
          >
            {[-1, 1].map((side) => (
              <path
                key={side}
                d={BROW}
                transform={`translate(100 100) scale(${-side} 1)`}
                fill="none"
                stroke={GLOW_CORE}
                strokeWidth={BROW_W}
                strokeLinecap="round"
              />
            ))}
          </g>

          <g
            style={{
              opacity: hearts ? 1 : 0,
              transition: "opacity 380ms cubic-bezier(.2,.8,.2,1)",
            }}
          >
            {[-1, 1].map((side) => (
              <g key={side} transform={`translate(${100 + side * EYE_X} ${100 + EYE_Y + 1}) scale(2.05)`}>
                {/* pops in with a little overshoot, then beats */}
                <g
                  style={{
                    transformBox: "fill-box",
                    transformOrigin: "center",
                    transform: `scale(${hearts ? 1 : 0.2})`,
                    transition: "transform 480ms cubic-bezier(.34,1.56,.64,1)",
                    transitionDelay: hearts ? `${side < 0 ? 120 : 190}ms` : "0ms",
                  }}
                >
                  <path
                    d={HEART}
                    fill={GLOW_CORE}
                    style={{
                      transformBox: "fill-box",
                      transformOrigin: "center",
                      animation: hearts ? "steffi-heartbeat 1.1s ease-in-out 0.6s infinite" : "none",
                    }}
                  />
                </g>
              </g>
            ))}
          </g>
        </g>
        <path
          ref={mouthRef}
          d={`M ${100 - MOUTH_HALF} ${100 + MOUTH_Y} Q 100 ${100 + MOUTH_Y + MOUTH_SAG} ${100 + MOUTH_HALF} ${100 + MOUTH_Y}`}
          fill="none"
          stroke={GLOW_CORE}
          strokeWidth={MOUTH_W}
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
}
